"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Button } from "@/src/components";
import { Toaster, toast } from "@/src/components/information/toast";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsToast() {
  return (
    <>
      {/* Toaster must be present for toasts to show */}
      <Toaster />

      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Toast
            </Text>
            <Badge variant="secondary">Information</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            Non-intrusive notifications that appear temporarily to provide feedback
            about an action or system status. Built on Sonner.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/toast" />
        </SectionCard>
      </Section>

      {/* Basic Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Usage</CardTitle>
              <CardDescription>Simple toast notifications</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap gap-4">
            <Button onClick={() => toast("This is a default toast")}>
              Show Toast
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast("Event has been created", {
                  description: "Monday, January 3rd at 6:00pm",
                })
              }
            >
              With Description
            </Button>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`import { toast } from "@/components/information/toast";`}</code>
            <code className="block mt-2">{`toast("This is a default toast");`}</code>
            <code className="block mt-2">{`toast("Event created", {`}</code>
            <code className="block">{`  description: "Monday, January 3rd at 6:00pm",`}</code>
            <code className="block">{`});`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Toast Types */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Toast Types</CardTitle>
              <CardDescription>Different toast variants for different contexts</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={() => toast.success("Successfully saved!")}
            >
              Success
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.error("Something went wrong")}
            >
              Error
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.warning("Please check your input")}
            >
              Warning
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.info("New update available")}
            >
              Info
            </Button>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`toast.success("Successfully saved!");`}</code>
            <code className="block">{`toast.error("Something went wrong");`}</code>
            <code className="block">{`toast.warning("Please check your input");`}</code>
            <code className="block">{`toast.info("New update available");`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* With Actions */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>With Actions</CardTitle>
              <CardDescription>Toasts with action buttons</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={() =>
                toast("File deleted", {
                  action: {
                    label: "Undo",
                    onClick: () => toast.success("File restored"),
                  },
                })
              }
            >
              With Action
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast("Meeting scheduled", {
                  description: "Team sync at 3pm",
                  cancel: {
                    label: "Cancel",
                    onClick: () => toast("Meeting cancelled"),
                  },
                })
              }
            >
              With Cancel
            </Button>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`toast("File deleted", {`}</code>
            <code className="block">{`  action: {`}</code>
            <code className="block">{`    label: "Undo",`}</code>
            <code className="block">{`    onClick: () => toast.success("File restored"),`}</code>
            <code className="block">{`  },`}</code>
            <code className="block">{`});`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Promise Toast */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Promise Toast</CardTitle>
              <CardDescription>Automatic loading, success, and error states</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={() => {
                const promise = new Promise((resolve) =>
                  setTimeout(resolve, 2000)
                );
                toast.promise(promise, {
                  loading: "Saving...",
                  success: "Settings saved!",
                  error: "Could not save",
                });
              }}
            >
              Promise (Success)
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const promise = new Promise((_, reject) =>
                  setTimeout(() => reject(new Error("Failed")), 2000)
                );
                toast.promise(promise, {
                  loading: "Processing...",
                  success: "Done!",
                  error: "Something went wrong",
                });
              }}
            >
              Promise (Error)
            </Button>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`toast.promise(myAsyncFunction(), {`}</code>
            <code className="block">{`  loading: "Saving...",`}</code>
            <code className="block">{`  success: "Settings saved!",`}</code>
            <code className="block">{`  error: "Could not save",`}</code>
            <code className="block">{`});`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Setup */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Setup</CardTitle>
              <CardDescription>Add Toaster to your root layout</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Text as="p" className="text-muted-foreground">
            Add the <code className="bg-muted px-1 rounded">Toaster</code> component to your root layout
            (e.g., in your Scaffold or App component) to enable toast notifications throughout your app.
          </Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`import { Toaster } from "@/registry/new-york/ui/toast";`}</code>
            <code className="block mt-2">{`function App() {`}</code>
            <code className="block">{`  return (`}</code>
            <code className="block">{`    <>`}</code>
            <code className="block">{`      <Toaster />`}</code>
            <code className="block">{`      {/* rest of your app */}`}</code>
            <code className="block">{`    </>`}</code>
            <code className="block">{`  );`}</code>
            <code className="block">{`}`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Toast Options</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Option</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">description</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 text-muted-foreground">Additional description text</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">duration</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">number</td>
                <td className="py-2 text-muted-foreground">Duration in ms (default: 4000)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">action</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">{`{ label, onClick }`}</td>
                <td className="py-2 text-muted-foreground">Action button configuration</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">cancel</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">{`{ label, onClick }`}</td>
                <td className="py-2 text-muted-foreground">Cancel button configuration</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">onDismiss</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">function</td>
                <td className="py-2 text-muted-foreground">Callback when toast is dismissed</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
